import { SelectField } from '@clay-run/glaze/select-field';
import { InputField } from '@clay-run/glaze/input-field';
import { TextareaField } from '@clay-run/glaze/textarea-field';
import { Text } from '@clay-run/glaze/text';
import type { EnrollmentItem, InputFieldOptions, SenderOption } from '@shared/flow/enrollment';
import type { EnrollmentItemDraft } from './EnrollmentItemEditor';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';

/**
 * The shared per-lead field cluster (bead .4, U4) — extracted OUT of
 * `EnrollmentItemEditor` so the sidebar's edit-in-place editor and the
 * full-screen review render the exact same Sender/connector-picker/Subject/
 * Copy markup with zero duplication (coding-standards.md: "no duplicated
 * field markup — if it renders in two places it is one component"). This is
 * also the seam a future per-step Gong flow editor (plan U13) mounts into
 * instead of this default field set.
 *
 * Props-driven, ZERO context reads (coding-standards.md layer-boundary
 * rule for a component "intended to be reused in two mounts") — every
 * effect that WRITES (autosave scheduling, the connector/channel
 * auto-default, the manual-pick race guard) stays owned by
 * `EnrollmentItemEditor`, which is the only caller today. This component is
 * pure render + the three plain-value change callbacks below.
 *
 * Glaze conversion (design-fidelity plan D10): this component used to be
 * deliberately pre-Glaze (hand-rolled `<label>`/`<select>`/`<input>`/
 * `<textarea>`) so the extraction landed without a styling detour. That
 * deferral is done — every field below is a real `@clay-run/glaze`
 * component, verified against the Storybook MCP server plus
 * `node_modules/@clay-run/glaze/dist` type definitions (the same convention
 * `BaseCopyEditor.tsx` follows), so the per-item editor and the play-level
 * `BaseCopyEditor` above it read as one visual system. Two behaviors that
 * are load-bearing, not styling, survive the conversion unchanged:
 *   - a sender/option value the current list no longer contains (a value
 *     picked before the picker existed, or an SFDC row removed under the
 *     rep) is injected as a synthetic extra item so the field still shows
 *     it instead of rendering blank;
 *   - a server-resolved options field (`inputOptions`) shows its placeholder
 *     ONLY while nothing is selected — `SelectField`'s own null-vs-item
 *     matching gives this for free (no item carries a `null` value, so an
 *     unset field always falls through to the placeholder, and a set field
 *     never can).
 * `locked`/`lockedTitle` map to `disabled` + a wrapping `title` — Base UI's
 * `Select` does not forward an ad hoc `title` prop to its trigger button
 * (verified: it lands on `Select.Root`, not the DOM node, and TypeScript
 * rejects it outright), so the two select fields below are wrapped in a
 * plain `<div title={lockedTitle}>` to keep the native hover tooltip;
 * `InputField`/`TextareaField` wrap a real `<input>`/`<textarea>` and take
 * `title` directly.
 */

/**
 * Display copy for a server-resolved options field, keyed by its input-schema
 * field key. See `EnrollmentItemEditor`'s original doc comment (unchanged by
 * this extraction) for why unknown keys fall back to the raw key.
 */
const INPUT_OPTION_FIELD_LABELS: Record<string, string> = {
  'Warm Intro Id': 'Intro from',
  'Preferred Channel': 'Preferred channel',
};

/** Reads a server-resolved options field's CURRENT persisted value off the item. */
function persistedValueFor(item: EnrollmentItem, persistsTo: InputFieldOptions['persistsTo']): string | null {
  return persistsTo === 'preferred_channel' ? item.preferred_channel : item.warm_intro_id;
}

export interface ItemFieldsProps {
  item: EnrollmentItem;
  draft: EnrollmentItemDraft;
  locked: boolean;
  lockedTitle?: string;
  contactLabel: string;
  senders: SenderOption[];
  inputOptions: InputFieldOptions[];
  hasEditableCopy: boolean;
  /**
   * See `PlayOption.copy_required`'s doc comment. Only meaningful alongside
   * `hasEditableCopy: true`; gates the required-asterisk on the Subject/Copy
   * labels below.
   */
  copyRequired: boolean;
  onSenderChange: (value: string) => void;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  /** `value` is `null` for "clear the selection" (the placeholder option). */
  onInputOptionChange: (field: InputFieldOptions, value: string | null) => void;
}

export function ItemFields({
  item,
  draft,
  locked,
  lockedTitle,
  contactLabel,
  senders,
  inputOptions,
  hasEditableCopy,
  copyRequired,
  onSenderChange,
  onSubjectChange,
  onBodyChange,
  onInputOptionChange,
}: ItemFieldsProps) {
  // An item saved before the picker existed may hold a value not in the
  // list — keep it selectable (as a synthetic extra item) so the field
  // doesn't render blank while a stale value is actually set.
  const senderItems = draft.sender && !senders.some(s => s.value === draft.sender)
    ? [{ value: draft.sender, label: draft.sender }, ...senders]
    : senders;

  return (
    <div className="grid gap-2">
      <div title={lockedTitle}>
        {/* Sender is required for EVERY play type (see `BaseCopyEditor.tsx`'s
            matching Sender label comment), so this per-lead Sender always
            carries the required-asterisk too, reusing the same
            dark-mode-aware convention. */}
        <SelectField
          label={<>Sender <span className="text-content-danger">*</span> <span className="font-normal text-content-tertiary">— per lead</span></>}
          aria-label={`Sender for ${contactLabel}`}
          items={senderItems}
          value={draft.sender}
          onValueChange={(value: string | null) => onSenderChange(value ?? '')}
          placeholder="Select sender…"
          disabled={locked}
          size="xs"
          // Glaze parity (rail-glaze-parity Change 1) — `size="xs"` alone
          // already matches `StepRow`'s `text-xs`; the trigger's border
          // width/radius are NOT exposed as a variant, but `className` DOES
          // reach the trigger (`Select`'s `styles.trigger({ className })`,
          // merged via tailwind-variants' twMerge), so these two utilities
          // override the trigger's default `rounded-sm`/`border` (1px) to
          // read as the same bordered-box family as `StepRow`'s
          // `rounded-md border-[0.5px] border-border-primary` boxes. The
          // trigger's vertical padding (`value` slot's own `py-[...]`) is a
          // SEPARATE slot `className` cannot reach — left at Glaze's default.
          className="rounded-md border-[0.5px] border-border-primary"
        />
      </div>
      {/* U8 / Task 4 (round-2 QA feedback) — server-resolved options fields
          (warm_intro's "Intro from" connector list, and the Preferred
          Channel picklist). A THIRD, independent branch: it does NOT hang
          off `hasEditableCopy` (warm_intro has none and still needs this)
          and does NOT hang off the sender select. R2: ALWAYS a dropdown,
          single-option included. */}
      {inputOptions.map(field => {
        const fieldLabel = INPUT_OPTION_FIELD_LABELS[field.key] ?? field.key;
        const selectedOptionValue = persistedValueFor(item, field.persistsTo);
        const known = field.options.some(o => o.value === selectedOptionValue);
        // Sender-select precedent: a persisted value missing from the live
        // list (the SFDC row was removed under the rep) stays selectable so
        // the field never renders blank while a value IS set.
        const fieldItems = selectedOptionValue !== null && !known
          ? [{ value: selectedOptionValue, label: `${selectedOptionValue} — no longer listed` }, ...field.options]
          : field.options;
        const placeholder = field.options.length === 0
          ? 'No connectors found for this contact'
          : `Select ${fieldLabel.toLowerCase()}…`;
        return (
          <div key={field.key} title={lockedTitle}>
            <SelectField<string | null>
              label={<>{fieldLabel} <span className="font-normal text-content-tertiary">— per lead</span></>}
              aria-label={`${fieldLabel} for ${contactLabel}`}
              items={fieldItems}
              value={selectedOptionValue}
              onValueChange={(value: string | null) => onInputOptionChange(field, value)}
              placeholder={placeholder}
              disabled={locked}
              size="xs"
              // See the Sender SelectField's matching comment above (Change 1).
              className="rounded-md border-[0.5px] border-border-primary"
            />
          </div>
        );
      })}
      {/* Copy fields render only when the play type declares rep-editable
          input (`input.*`) fields. A play like generate_message resolves
          everything server-side, so Subject/Copy would be inert — hidden. */}
      {hasEditableCopy && (
        <>
          {/* Required-asterisk only when `copyRequired` (event_invite).
              `warm_intro` has `hasEditableCopy: true` but
              `copyRequired: false` — a blank draft there legitimately falls
              back to a server-rendered template at send time, so no star. */}
          <InputField
            {...AUTOFILL_IGNORE_PROPS}
            label={copyRequired ? <>Subject <span className="text-content-danger">*</span></> : 'Subject'}
            aria-label={`Subject for ${contactLabel}`}
            value={draft.subject}
            onValueChange={(value: string) => onSubjectChange(value)}
            disabled={locked}
            title={lockedTitle}
            size="sm"
          />
          <TextareaField
            {...AUTOFILL_IGNORE_PROPS}
            label={copyRequired ? <>Copy <span className="text-content-danger">*</span></> : 'Copy'}
            aria-label={`Copy for ${contactLabel}`}
            rows={3}
            value={draft.body}
            onValueChange={(value: string) => onBodyChange(value)}
            disabled={locked}
            title={lockedTitle}
            size="sm"
          />
          <Text size="xs" variant="tertiary">
            <code>{'{{Firstname}}'}</code>/<code>{'{{Account}}'}</code> are sent literally — Clay merges them per recipient.
          </Text>
        </>
      )}
    </div>
  );
}
